import {useMemo, useState} from "react";
import {Chip, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import {CCMemberStat} from "../../../data/internal-affairs/ComplaintStatisticsResponse";

const cols: { key: keyof CCMemberStat; label: string }[] = [
    {key: "nick",           label: "Member"},
    {key: "ccRole",         label: "Role"},
    {key: "rankDisplayName",label: "Rank"},
    {key: "totalAssigned",  label: "Assigned"},
    {key: "voted",          label: "Voted"},
    {key: "notVoted",       label: "Did Not Vote"},
    {key: "pending",        label: "Pending"},
]

export function CCOverviewTab({stats}: { stats: CCMemberStat[] }) {
    const [sort, setSort] = useState<keyof CCMemberStat>("totalAssigned")

    const sorted = useMemo(() =>
        [...stats].sort((a, b) => {
            const av = a[sort] ?? 0
            const bv = b[sort] ?? 0
            return typeof av === "number" && typeof bv === "number" ? bv - av : String(av).localeCompare(String(bv))
        }), [stats, sort])

    const voteRate = (s: CCMemberStat) =>
        s.totalAssigned > 0 ? Math.round((s.voted / s.totalAssigned) * 100) : null

    return (
        <Paper variant="outlined" sx={{overflowX: "auto"}}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {cols.map(c => (
                            <TableCell key={c.key} onClick={() => setSort(c.key)}
                                       sx={{cursor: "pointer", fontWeight: sort === c.key ? 700 : 400,
                                            whiteSpace: "nowrap", userSelect: "none"}}>
                                {c.label}{sort === c.key ? " ↓" : ""}
                            </TableCell>
                        ))}
                        <TableCell sx={{whiteSpace: "nowrap"}}>Vote Rate</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sorted.map(s => {
                        const rate = voteRate(s)
                        return (
                            <TableRow key={s.communityId}>
                                <TableCell>
                                    <Link underline="none" component={RouterLink} to={`/user/${s.communityId}`}
                                          variant="body2" fontWeight={500}>{s.nick}</Link>
                                </TableCell>
                                <TableCell>
                                    <Chip label={s.ccRole ?? "—"} size="small" variant="outlined" sx={{fontSize: "0.7rem"}}/>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">{s.rankDisplayName ?? "—"}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip label={s.totalAssigned} size="small"
                                          color={s.totalAssigned > 0 ? "primary" : "default"}
                                          variant={s.totalAssigned > 0 ? "filled" : "outlined"}/>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="success.main">{s.voted}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color={s.notVoted > 0 ? "error.main" : "text.disabled"}>
                                        {s.notVoted}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color={s.pending > 0 ? "warning.main" : "text.disabled"}>
                                        {s.pending}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {rate !== null
                                        ? <Typography variant="body2" color={rate >= 80 ? "success.main" : rate >= 50 ? "warning.main" : "error.main"}>
                                            {rate}%
                                          </Typography>
                                        : <Typography variant="body2" color="text.disabled">—</Typography>
                                    }
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </Paper>
    )
}
